<template>
  <li
    class="toast"
    :class="`toast--${notification.type}`"
    role="alert"
    @mouseenter="paused = true"
    @mouseleave="paused = false"
  >
    <div class="toast__side"></div>

    <div class="toast__icon" aria-hidden="true">
      <!-- success -->
      <svg v-if="notification.type === 'success'" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="10" cy="10" r="8"/>
        <polyline points="6 10 9 13 14 7"/>
      </svg>
      <!-- warning -->
      <svg v-else-if="notification.type === 'warning'" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10 3L18 17H2L10 3z"/>
        <line x1="10" y1="9" x2="10" y2="12"/>
        <line x1="10" y1="14.5" x2="10" y2="15"/>
      </svg>
      <!-- accent (scenario events) -->
      <svg v-else-if="notification.type === 'accent'" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="10 2 12.4 7.5 18.5 8.2 14 12.5 15.3 18.5 10 15.5 4.7 18.5 6 12.5 1.5 8.2 7.6 7.5"/>
      </svg>
      <!-- info (default) -->
      <svg v-else viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="10" cy="10" r="8"/>
        <line x1="10" y1="9" x2="10" y2="14"/>
        <circle cx="10" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
      </svg>
    </div>

    <div class="toast__content">
      <div class="toast__title">{{ notification.title }}</div>
      <div v-if="notification.body" class="toast__body">{{ notification.body }}</div>
    </div>

    <button class="toast__close" @click="$emit('dismiss', notification.id)" aria-label="Dismiss">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <line x1="4" y1="4" x2="12" y2="12"/>
        <line x1="12" y1="4" x2="4" y2="12"/>
      </svg>
    </button>

    <div class="toast__progress" :class="{ 'toast__progress--paused': paused }" :style="{ '--duration': DURATION + 'ms' }"></div>
  </li>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

const props  = defineProps({ notification: Object })
const emit   = defineEmits(['dismiss'])
const paused = ref(false)

const DURATION = 5000
let timer     = null
let remaining = DURATION
let startedAt = null

function startTimer() {
  startedAt = Date.now()
  timer = setTimeout(() => emit('dismiss', props.notification.id), remaining)
}

function stopTimer() {
  clearTimeout(timer)
  remaining -= Date.now() - startedAt
}

watch(paused, p => p ? stopTimer() : startTimer())

onMounted(startTimer)
onBeforeUnmount(() => clearTimeout(timer))
</script>

<style scoped>
.toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 320px;
  background: var(--color-surface-raised);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 0.75rem 0.75rem 0.75rem 0;
  overflow: hidden;
  list-style: none;
}

/* Coloured left accent strip */
.toast__side {
  width: 3px;
  align-self: stretch;
  flex-shrink: 0;
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
}

.toast--success .toast__side { background: var(--color-success); }
.toast--warning .toast__side { background: var(--color-warning); }
.toast--danger  .toast__side { background: var(--color-danger); }
.toast--accent  .toast__side,
.toast--info    .toast__side { background: var(--color-info); }

/* Icon */
.toast__icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
}
.toast__icon svg { width: 100%; height: 100%; }

.toast--success .toast__icon { color: var(--color-success); }
.toast--warning .toast__icon { color: var(--color-warning); }
.toast--danger  .toast__icon { color: var(--color-danger); }
.toast--accent  .toast__icon,
.toast--info    .toast__icon { color: var(--color-info); }

/* Content */
.toast__content {
  flex: 1;
  min-width: 0;
}

.toast__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.4;
}

.toast__body {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin-top: 0.15rem;
  line-height: 1.4;
}

/* Close */
.toast__close {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
  border-radius: var(--radius-sm);
  transition: color var(--transition-fast), background var(--transition-fast);
}
.toast__close:hover { color: var(--color-text); background: var(--color-border-subtle); }
.toast__close svg { width: 12px; height: 12px; }

/* Progress bar */
.toast__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 100%;
  transform-origin: left;
  animation: shrink var(--duration, 5000ms) linear forwards;
}
.toast__progress--paused { animation-play-state: paused; }

.toast--success .toast__progress { background: var(--color-success); }
.toast--warning .toast__progress { background: var(--color-warning); }
.toast--danger  .toast__progress { background: var(--color-danger); }
.toast--accent  .toast__progress,
.toast--info    .toast__progress { background: var(--color-info); }

@keyframes shrink {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}
</style>
